<template>
  <v-col>
    <v-row class="mb-4">
      <v-col>
        <h3 class="text-h6 font-weight-medium mb-4">
          Debug Information
        </h3>
        <p class="text--secondary">
          Developer tools and user object debugging information.
        </p>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card outlined>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Current User Object</span>
            <v-btn
              small
              outlined
              @click="copyToClipboard"
            >
              <v-icon
                left
                small
              >
                content_copy
              </v-icon>
              Copy JSON
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-expansion-panels
              v-model="expandedPanels"
              multiple
            >
              <!-- User Overview -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <strong>User Overview</strong>
                    <div class="caption text--secondary">
                      Basic user information and status
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-simple-table dense>
                    <tbody>
                      <tr>
                        <td class="font-weight-bold">
                          Email:
                        </td>
                        <td>{{ currentUser.email || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">
                          Name:
                        </td>
                        <td>{{ getUserFullName() }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">
                          Document ID:
                        </td>
                        <td>{{ currentUser.docId || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">
                          Is Active:
                        </td>
                        <td>
                          <v-chip
                            small
                            :color="currentUser.isActive ? 'green' : 'red'"
                            text-color="white"
                          >
                            {{ currentUser.isActive ? 'Active' : 'Inactive' }}
                          </v-chip>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">
                          Role:
                        </td>
                        <td>{{ currentUser.access.role || 'N/A' }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Permissions -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <strong>Permissions</strong>
                    <div class="caption text--secondary">
                      User permissions and access rights
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <pre class="text-body-2">{{ formatJSON(currentUser.access.permissions || {}) }}</pre>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Settings -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <strong>Settings</strong>
                    <div class="caption text--secondary">
                      User preferences and configuration
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <pre class="text-body-2">{{ formatJSON(currentUser.settings || {}) }}</pre>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Full User Object -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <strong>Complete User Object</strong>
                    <div class="caption text--secondary">
                      Full user data structure (use with caution)
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-alert
                    type="warning"
                    dense
                    text
                    class="mb-3"
                  >
                    This contains sensitive information. Do not share or log in production.
                  </v-alert>
                  <pre class="text-body-2 debug-json">{{ formatJSON(cleanedUserObject) }}</pre>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar for copy feedback -->
    <v-snackbar
      v-model="showCopySuccess"
      timeout="2000"
      color="success"
    >
      JSON copied to clipboard!
    </v-snackbar>
  </v-col>
</template>

<script>
export default {
  name: 'SettingsDebug',
  props: {
    currentUser: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedPanels: [0], // Start with first panel expanded
      showCopySuccess: false,
    }
  },
  computed: {
    cleanedUserObject() {
      if (!this.currentUser) return {}

      // Remove sensitive Firebase metadata
      const cleaned = { ...this.currentUser }
      const sensitiveFields = [
        'metadata',
        'multiFactor',
        'providerData',
        'refreshToken',
        'accessToken',
        'auth',
        'stsTokenManager',
        '_delegate',
      ]

      sensitiveFields.forEach((field) => {
        delete cleaned[field]
      })

      return cleaned
    },
  },
  methods: {
    formatJSON(obj) {
      if (!obj || typeof obj !== 'object') {
        return String(obj || 'null')
      }
      return JSON.stringify(obj, null, 2)
    },

    getUserFullName() {
      const { personNameFirst, personNameLast, personName } = this.currentUser
      if (personNameFirst && personNameLast) {
        return `${personNameFirst} ${personNameLast}`
      }
      return personName || 'N/A'
    },

    async copyToClipboard() {
      try {
        const jsonString = this.formatJSON(this.cleanedUserObject)
        await navigator.clipboard.writeText(jsonString)
        this.showCopySuccess = true
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = this.formatJSON(this.cleanedUserObject)
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.showCopySuccess = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.debug-json {
  max-height: 400px;
  overflow-y: auto;
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  line-height: 1.4;
}

pre {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
